/**
 * Pure helpers for the offline database export/restore path (no Dexie or Amplify imports so they are unit-testable).
 */

/**
 * Works out which tables an import into the live database must leave alone.
 * - Tables the app has but the JSON lacks (e.g. imageBinary, which the export skips on purpose) must not be cleared,
 *   otherwise a restore would wipe photo binaries that were hydrated separately.
 * - Tables the JSON has but the app no longer defines (e.g. `analytics` from an old export) must not be touched:
 *   Dexie 4's db.table() throws InvalidTable for unknown names before dexie-export-import's own
 *   acceptMissingTables check runs.
 * @param dbTableNames Table names defined by the app's Dexie schema
 * @param jsonTableNames Table names present in the export file
 * @returns Table names to pass as importInto's skipTables
 */
export function computeRestoreSkipTables(dbTableNames: string[], jsonTableNames: string[]): string[] {
	const dbSet = new Set(dbTableNames);
	const jsonSet = new Set(jsonTableNames);
	const dbOnly = dbTableNames.filter((name) => !jsonSet.has(name));
	const jsonOnly = jsonTableNames.filter((name) => !dbSet.has(name));
	return [...dbOnly, ...jsonOnly];
}

/**
 * Maps `done / total` onto a percentage window and clamps it to a whole number the progress toast accepts.
 * A missing or zero total yields the start of the window.
 */
export function windowedPercent(done: number, total: number | undefined, windowStart: number, windowEnd: number): number {
	if (!total || total <= 0 || !Number.isFinite(done)) return Math.round(windowStart);
	const ratio = Math.min(Math.max(done / total, 0), 1);
	return Math.round(windowStart + ratio * (windowEnd - windowStart));
}

/**
 * Builds the plain-text manifest written next to a database export so the upload can be verified from the S3 console.
 */
export function buildDbExportManifest(input: {
	username: string;
	uploadedAt: string;
	appVersion?: string;
	dbVersion: number;
	skippedTables: string[];
	rowCounts: Record<string, number>;
	jsonKey: string;
}): string {
	const totalRows = Object.values(input.rowCounts).reduce((sum, count) => sum + count, 0);
	const lines = [
		`uploadedBy: ${input.username}`,
		`uploadedAt: ${input.uploadedAt}`,
		`appVersion: ${input.appVersion ?? "unknown"}`,
		`dbVersion: ${input.dbVersion}`,
		`jsonKey: ${input.jsonKey}`,
		`skippedTables: ${input.skippedTables.join(", ")}`,
		"",
		"rowCounts:",
		...Object.entries(input.rowCounts).map(([table, count]) => `  ${table}: ${count}`),
		"",
		`totalRows: ${totalRows}`,
	];
	return lines.join("\n");
}
